import React, { useState } from 'react';
import { X, AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

/**
 * AlertBar - Renders header alerts (top center)
 *
 * Types: error, warning, confirm (success moved to footer)
 * 1 alert: renders normally
 * 2+ alerts: collapses to summary row (expanded by default), Dismiss All button
 */

const TYPE_PRIORITY = { error: 4, confirm: 3, warning: 2, info: 1 };

const getIcon = (type, size = 18) => {
  switch (type) {
    case 'error':   return <AlertCircle size={size} />;
    case 'warning': return <AlertTriangle size={size} />;
    case 'confirm': return <AlertTriangle size={size} />;
    case 'info':
    default:        return <Info size={size} />;
  }
};

const getWorstType = (alerts) => {
  return alerts.reduce((worst, a) => {
    return (TYPE_PRIORITY[a.type] || 0) > (TYPE_PRIORITY[worst] || 0) ? a.type : worst;
  }, 'info');
};

const AlertBar = () => {
  const { alerts, dismissAlert, notify } = useNotifications();
  const clearAlerts = notify.clearAlerts;
  const [isExpanded, setIsExpanded] = useState(false);

  if (alerts.length === 0) return null;

  const renderAlertItem = (alert) => (
    <div key={alert.id} className={`alert-item alert-${alert.type}`}>
      <span className="alert-icon">{getIcon(alert.type)}</span>
      <span className="alert-message">{alert.message}</span>
      {alert.type === 'confirm' ? (
        <div className="alert-actions">
          <button className="alert-btn alert-btn-confirm" onClick={() => { alert.onConfirm?.(); dismissAlert(alert.id); }}>
            {alert.confirmText}
          </button>
          <button className="alert-btn alert-btn-cancel" onClick={() => { alert.onCancel?.(); dismissAlert(alert.id); }}>
            {alert.cancelText}
          </button>
        </div>
      ) : (
        <button className="alert-dismiss" onClick={() => dismissAlert(alert.id)} title="Dismiss">
          <X size={16} />
        </button>
      )}
    </div>
  );

  // Single alert — render normally
  if (alerts.length === 1) {
    return (
      <div className="alert-bar">
        {renderAlertItem(alerts[0])}
      </div>
    );
  }

  // 2+ alerts — replace everything with collapsed stack indicator
  const worstType = getWorstType(alerts);
  return (
    <div className="alert-bar">
      <div className={`alert-item alert-summary alert-${worstType}`}>
        <span className="alert-icon">{getIcon(worstType)}</span>
        <span className="alert-message alert-summary-label">
          {alerts.length} alerts
        </span>
        <div className="alert-summary-actions">
          <button className="alert-btn alert-btn-dismiss-all" onClick={clearAlerts}>
            Dismiss All
          </button>
          <button className="alert-dismiss" onClick={() => setIsExpanded(e => !e)} title={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="alert-stack">
          {alerts.map(renderAlertItem)}
        </div>
      )}
    </div>
  );
};

export default AlertBar;
